import React, { useEffect, useState } from 'react';
import TeacherNavbar from '../components/TeacherNavbar';
import { useTheme } from '@mui/material/styles';
import {
  Divider,
  Modal, 
  Box,
  Badge,
  Tabs,
  Tab,
  useMediaQuery,
  InputBase,
  Button,
  Chip,
  ButtonBase,
} from '@mui/material';
import Typography from '@mui/material/Typography';
import moduleTicketImg from '../img/ModuleImg.jpeg';
import { useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { db } from '../firebaseConfig';
import { signOut } from 'firebase/auth';
import { collection, doc, getDoc, query, where, getDocs, onSnapshot } from 'firebase/firestore';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded';
import TeacherFAQ from '../components/TeacherFAQ';
import TeacherEvents from '../components/TeacherEvents';
import TeacherNews from '../components/TeacherNews';
import ForumRoundedIcon from '@mui/icons-material/ForumRounded';
import FeedbackRoundedIcon from '@mui/icons-material/FeedbackRounded';
import TeacherMessagesBox from '../components/TeacherMessagesBox';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import HomepageImg from "../img/HomePageImg.webp";
import { useTranslation } from 'react-i18next';
import { getRoleLabels, getStoredTutorType, setStoredTutorType } from '../utils/roleTerminology';


function TeacherClasses() {

    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
    const isMedium = useMediaQuery(theme.breakpoints.between('sm', 'md'));
    const iconSize = 30;
    const gapSize = isSmall ? '12px' : isMedium ? '25px' : '40px';
    const navigate = useNavigate();
    const [firstName, setFirstName] = useState('');
    const [open, setOpen] = useState(false);
    const [modalContent, setModalContent] = useState('');
    const [classes, setClasses] = useState([]);
    const [error, setError] = useState('');
    const [msgCount, setMsgCount] = useState(0); 
    const [tabFilter, setTabFilter] = useState('published'); 
    const [sortBy, setSortBy] = useState('alpha');
    const [searchText, setSearchText] = useState('');
    const [upcomingMeetings, setUpcomingMeetings] = useState([]);
    const [unreadConversations, setUnreadConversations] = useState([]);
    const auth = getAuth();
    const role = getRoleLabels(t, getStoredTutorType());
  

    // Fetch the current user's first name
    useEffect(() => {
       const fetchFirstName = async () => {
         const currentUser = auth.currentUser;
         if (!currentUser) {
           console.error('No user logged in.');
           return;
          }
          
         const userUID = currentUser.uid;
         try {
          const studentQuery = query(collection(db, 'teachers'), where('uid', '==', userUID));
           const querySnapshot = await getDocs(studentQuery);
          
            if (!querySnapshot.empty) {
             const studentData = querySnapshot.docs[0].data();
              setFirstName(studentData.firstName);
              setStoredTutorType(studentData?.tutorType || "Teacher");
           } else {
            console.error('No matching student found.');
          }
          } catch (error) {
            console.error('Error fetching student data:', error);
          }
       };
          
      fetchFirstName();
    }, []);


    // ✅ Chat unread count (Teacher): sum of conversations.unreadCountTeacher for this teacher UID
    useEffect(() => {
      const user = auth.currentUser;
      if (!user) return;

      const qConvos = query(
        collection(db, "conversations"),
        where("teacherId", "==", user.uid)
      );

      const unsub = onSnapshot(
        qConvos,
        (snap) => {
          let total = 0;
          const unread = [];
          snap.forEach((d) => {
            const data = d.data() || {};
            const unreadCount = Number(data.unreadCountTeacher || 0);
            total += unreadCount;
            if (unreadCount > 0) {
              unread.push({ id: d.id, ...data, unreadCountTeacher: unreadCount });
            }
          });
          setMsgCount(total);
          unread.sort((a, b) => {
            const aT = a.lastMessageAt?.toMillis?.() || 0;
            const bT = b.lastMessageAt?.toMillis?.() || 0;
            return bT - aT;
          });
          setUnreadConversations(unread);
        },
        (err) => {
          console.error("Teacher unread chat listener error:", err);
          setMsgCount(0);
          setUnreadConversations([]);
        }
      );

      return () => unsub();
    }, []);


   // Open Modal
   const handleOpen = (content) => {
    setModalContent(content);
    setOpen(true);
  };

   // Close Modal
   const handleClose = () => {
    setOpen(false);
  };

  const initialsFromName = (fullName = '') => {
    const parts = fullName.trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return '?';
    const first = parts[0][0] || '';
    const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
    const joined = `${first}${last || ''}`.trim();
    return joined ? joined.toUpperCase() : '?';
  };

  const AVATAR_COLORS = [
    { bg: '#e0f2fe', color: '#0b5394' },
    { bg: '#fef3c7', color: '#92400e' },
    { bg: '#ecfccb', color: '#365314' },
    { bg: '#e0f2f1', color: '#0f766e' },
    { bg: '#f3e8ff', color: '#6b21a8' },
    { bg: '#fee2e2', color: '#991b1b' },
  ];

  const avatarColorsForName = (fullName = '') => {
    if (!fullName) return AVATAR_COLORS[0];
    const hash = fullName
      .split('')
      .reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
    return AVATAR_COLORS[hash % AVATAR_COLORS.length];
  };

  // Parse "YYYY/MM/DD" + "HH:MM" -> timestamp (ms)
  const toStartMillis = (dateStr, timeStr) => {
    if (!dateStr || !timeStr) return null;
    const [y, m, d] = dateStr.split('/').map(Number);
    const [hh, mm] = timeStr.split(':').map(Number);
    return new Date(y, m - 1, d, hh, mm, 0, 0).getTime();
  };

  const getDayMonth = (dateStr) => {
    if (!dateStr) return { day: '--', month: '' };
    const [y, m, d] = dateStr.split('/').map(Number);
    const dateObj = new Date(y, m - 1, d);
    const day = Number.isNaN(dateObj.getTime()) ? '--' : String(dateObj.getDate());
    const month = Number.isNaN(dateObj.getTime())
      ? ''
      : dateObj.toLocaleString(i18n.language, { month: 'short' });
    return { day, month };
  };

  // Open link in new tab; add https:// if missing
  const openMeetingLink = (url) => {
    if (!url) return;
    const fullUrl = url.startsWith('http://') || url.startsWith('https://') ? url : `https://${url}`;
    window.open(fullUrl, '_blank', 'noopener,noreferrer');
  };

  const fetchUpcomingMeetings = async (teacherUid, groupIds) => {
    if (!teacherUid || !Array.isArray(groupIds) || groupIds.length === 0) {
      setUpcomingMeetings([]);
      return;
    }
    try {
      const meetingPromises = groupIds.map(async (groupId) => {
        const snap = await getDocs(collection(db, 'meetings', teacherUid, groupId));
        const items = [];
        snap.forEach((docSnap) => {
          const data = docSnap.data();
          const startMs = toStartMillis(data?.date, data?.startTime);
          items.push({ id: docSnap.id, groupId, ...data, startMs });
        });
        return items;
      });

      const groupMeetings = await Promise.all(meetingPromises);
      const allMeetings = groupMeetings.flat();
      const now = Date.now();
      const upcomingList = allMeetings
        .filter((m) => m.startMs && m.startMs >= now)
        .sort((a, b) => a.startMs - b.startMs);
      setUpcomingMeetings(upcomingList);
    } catch (err) {
      console.error('Error fetching upcoming meetings:', err);
      setUpcomingMeetings([]);
    }
  };

   // Fetch all teacher classes
   const fetchClasses = async () => {
      try {
        const user = auth.currentUser;
        if (!user) {
          setError(t('يجب على المستخدم فتح حسابه الخاص'));
          return;
        }
  
        // Step 1: Fetch teacher data by UID
        const teacherQuery = query(
          collection(db, 'teachers'),
          where('uid', '==', user.uid)
        );
  
        const querySnapshot = await getDocs(teacherQuery);
        if (querySnapshot.empty) {
          setError(t('لم يتم العثور على بيانات الأستاذ للمستخدم الحالي'));
          console.warn("No teacher data found. Signing out the user...");

          // Sign the user out
          await signOut(auth);
          return;
        }
  
        const teacherData = querySnapshot.docs[0].data();
        const groupIds = teacherData.groupIds || []; 
        fetchUpcomingMeetings(user.uid, groupIds);
  
        // Step 2: Fetch group details by ID
        const groupPromises = groupIds.map(async (groupId) => {
          const groupRef = doc(db, 'groups', groupId); 
          const groupSnap = await getDoc(groupRef);
          if (groupSnap.exists()) {
            const groupData = groupSnap.data();
            return {
              id: groupId,
              name: groupData.name,
              groupImage: groupData.groupImage || '',
              studentCount: groupData.studentIds ? groupData.studentIds.length : 0, 
            };
          }
          return null;
        });
  
        const groupDetails = await Promise.all(groupPromises);
        setClasses(groupDetails.filter((group) => group !== null)); 
      } catch (err) {
        console.error('Error fetching classes:', err);
        setError(t('حدث خطأ أثناء جلب الأقسام'));
      }
    };
  
    useEffect(() => {
      fetchClasses();
    }, []);
  


  const handleClassClick = (groupId) => {
     navigate(`/teacher/class/${groupId}`);
   };

  const normalizedSearch = searchText.trim().toLowerCase();
  const filteredClasses = normalizedSearch
    ? classes.filter((classData) =>
        (classData?.name || '').toLowerCase().includes(normalizedSearch)
      )
    : classes;
     

  return (

    <div
      style={{
        background:
          'radial-gradient(1200px 600px at 10% -10%, rgba(255, 204, 155, 0.35), transparent 60%), radial-gradient(900px 500px at 100% 0%, rgba(130, 189, 255, 0.35), transparent 60%), #f6f7fb',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: '"Space Grotesk", "Work Sans", system-ui, sans-serif',
        overflowX: 'hidden',
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      <TeacherNavbar />

      <Box
        sx={{
          px: { xs: 2, sm: 3, md: 5 },
          py: { xs: 3, md: 4 },
          maxWidth: 1280,
          mx: 'auto',
          width: '100%',
          minWidth: 0,
          boxSizing: 'border-box',
          overflowX: 'hidden',
        }}
      >
        {/* Top Meta + Search */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
            alignItems: { xs: 'flex-start', md: 'center' },
            justifyContent: 'space-between',
            mb: 1,
            flexWrap: 'wrap',
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap',
              flex: { xs: '0 0 auto', md: '1 1 520px' },
              width: { xs: '100%', md: 'auto' },
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.5,
                py: 0.75,
                borderRadius: 3,
                bgcolor: '#fff',
                border: '1px solid #eef1f6',
                boxShadow: '0 8px 18px rgba(15, 23, 42, 0.05)',
                flex: { xs: '1 1 100%', md: '1 1 320px' },
                minWidth: 0,
                maxWidth: { md: 420 },
              }}
            >
              <SearchRoundedIcon sx={{ opacity: 0.6 }} />
              <InputBase
                placeholder={role.isCoach ? t('Search programs…') : t('Search classes…')}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                sx={{ color: '#0f172a', width: '100%' }}
              />
            </Box>
            <ButtonBase
              onClick={() => navigate('/teacher/students')}
              aria-label={role.isCoach ? t('Go to clients page') : t('Go to students page')}
              sx={{
                width: 46,
                height: 46,
                borderRadius: 2,
                bgcolor: '#fff',
                border: '1px solid #eef1f6',
                boxShadow: '0 8px 18px rgba(15, 23, 42, 0.05)',
                display: 'grid',
                placeItems: 'center',
                color: '#0f172a',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 12px 24px rgba(15, 23, 42, 0.12)' },
              }}
            >
              <PersonAddIcon />
            </ButtonBase>
            {!role.isCoach && (
              <ButtonBase
                onClick={() => navigate('/teacher/groups')}
                aria-label={t('Go to groups page')}
                sx={{
                  width: 46,
                  height: 46,
                  borderRadius: 2,
                  bgcolor: '#fff',
                  border: '1px solid #eef1f6',
                  boxShadow: '0 8px 18px rgba(15, 23, 42, 0.05)',
                  display: 'grid',
                  placeItems: 'center',
                  color: '#0f172a',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 12px 24px rgba(15, 23, 42, 0.12)' },
                }}
              >
                <GroupsRoundedIcon />
              </ButtonBase>
            )}
          </Box>
           <Box sx={{ flex: { xs: '0 0 auto', md: '1 1 260px' }, width: { xs: '100%', md: 'auto' }, minWidth: 0, textAlign: 'right', alignItems: 'flex-end' }}>
            <Typography sx={{ color: '#64748b', fontWeight: 600, fontSize: 14, textAlign: 'right' }}>
              {new Date().toLocaleDateString(i18n.language, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#0f172a', textAlign: 'right' }}>
              {t('أهلاً {{name}}', { name: firstName })}
            </Typography>
          </Box>
        </Box>

        {/* Top Category / Quick Stats Cards */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' },
            gap: 2,
            mb: 3,
            minWidth: 0,
            width: '100%',
          }}
        >
          {[
            { label: role.isCoach ? t('Total programs') : t('Total classes'), value: classes.length, tone: '#eef2ff' },
            { label: role.isCoach ? t('Total clients') : t('Total students'), value: classes.reduce((sum, c) => sum + (c?.studentCount || 0), 0), tone: '#ecfeff' },
            { label: t('Active groups'), value: classes.length, tone: '#fef3c7' },
            { label: t('Unread messages'), value: msgCount, tone: '#fee2e2' },
          ].map((item, idx) => (
            <Box
              key={`${item.label}-${idx}`}
              sx={{
                bgcolor: '#fff',
                borderRadius: 2.5,
                p: 2.25,
                border: '1px solid #eef1f6',
                boxShadow: '0 10px 24px rgba(15, 23, 42, 0.06)',
                display: 'grid',
                gap: 1,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography sx={{ fontWeight: 600, color: '#505153ff' }}>{item.label}</Typography>
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    border: '1px solid #e5e7eb',
                    display: 'grid',
                    placeItems: 'center',
                    color: '#0f172a',
                    fontSize: 16,
                    lineHeight: 1,
                  }}
                >
                  ↗
                </Box>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#0f172a' }}>
                {item.value}
              </Typography>
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 2.2fr) minmax(0, 1fr)' },
            gap: 3,
            minWidth: 0,
            width: '100%',
          }}
        >
          {/* Left Column */}
          <Box sx={{ display: 'grid', gap: 3, minWidth: 0 }}>
            {/* My Classes Overview (Featured Grid style) */}
            <Box
              sx={{
                bgcolor: '#fff',
                borderRadius: 2.5,
                p: 3,
                border: '1px solid #eef1f6',
                boxShadow: '0 10px 24px rgba(15, 23, 42, 0.06)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#0f172a' }}>
                  {role.isCoach ? t('My Programs Overview') : t('My Classes Overview')}
                </Typography>
              </Box>
              <Divider sx={{ my: 2 }} />
              {error ? (
                <Typography color="error" variant="body2">
                  {error}
                </Typography>
              ) : filteredClasses.length === 0 ? (
                <Typography sx={{ color: '#64748b' }}>
                  {normalizedSearch
                    ? role.isCoach ? t('No matching programs found.') : t('No matching classes found.')
                    : role.isCoach ? t('No programs assigned yet.') : t('No classes assigned yet.')}
                </Typography>
              ) : (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' },
                    gap: 2,
                    minWidth: 0,
                  }}
                >
                  {filteredClasses.map((classData) => (
                    <Box
                      key={classData.id}
                      sx={{
                        borderRadius: 0.5,
                        overflow: 'hidden',
                        border: '1px solid #eef1f6',
                        bgcolor: '#fff',
                        boxShadow: '0 10px 24px rgba(15, 23, 42, 0.06)',
                        display: 'grid',
                      }}
                    >
                      <Box sx={{ position: 'relative', height: 150 }}>
                        <img
                          src={classData.groupImage || moduleTicketImg}
                          alt={t('Class Thumbnail')}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      </Box>
                      <Box sx={{ p: 2, display: 'grid', gap: 0.8 }}>
                        <Typography sx={{ fontWeight: 700, color: '#0f172a' }}>{classData.name}</Typography>
                        <Typography sx={{ color: '#64748b', fontSize: 13 }}>
                          {t('Subject')}: {t('Not set')}
                        </Typography>
                        <Typography sx={{ color: '#64748b', fontSize: 13 }}>
                          {t('Level')}: {t('Not set')}
                        </Typography>
                        <Typography sx={{ color: '#64748b', fontSize: 13 }}>
                          {role.learnerPlural}: {classData.studentCount}
                        </Typography>
                        <Button
                          variant="outlined"
                          onClick={() => handleClassClick(classData.id)}
                          sx={{
                            mt: 1,
                            borderRadius: 1.5,
                            textTransform: 'none',
                            fontWeight: 700,
                            borderColor: '#0f172a',
                            color: '#0f172a',
                            '&:hover': { borderColor: '#0f172a', bgcolor: '#eef2f7' },
                          }}
                        >
                          {role.isCoach ? t('View program') : t('View class')}
                        </Button>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
            {/* Today’s Schedule */}
            <Box
              sx={{
                bgcolor: '#fff',
                borderRadius: 2.5,
                p: 3,
                border: '1px solid #eef1f6',
                boxShadow: '0 10px 24px rgba(15, 23, 42, 0.06)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#0f172a' }}>
                  {t('Upcoming meetings')}
                </Typography>
              </Box>
              <Divider sx={{ my: 2 }} />
              {upcomingMeetings.length === 0 ? (
                <Typography sx={{ color: '#64748b' }}>
                  {t('No upcoming meetings')}
                </Typography>
              ) : (
                <Box sx={{ display: 'grid', gap: 1.5 }}>
                  {upcomingMeetings.slice(0, 3).map((meeting) => {
                    const { day, month } = getDayMonth(meeting.date);
                    return (
                    <Box
                      key={`meeting-${meeting.id}`}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1.5,
                        p: 2,
                        borderRadius: 2,
                        border: '1px solid #eef1f6',
                        background: '#f8fafc',
                        minWidth: 0,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
                        <Box sx={{ minWidth: 56, textAlign: 'center' }}>
                          <Typography sx={{ fontWeight: 700, fontSize: 28, color: '#0f172a', lineHeight: 1 }}>
                            {day}
                          </Typography>
                          <Typography sx={{ fontSize: 12, color: '#64748b', letterSpacing: 0.6, textTransform: 'uppercase' }}>
                            {month}
                          </Typography>
                        </Box>
                        <Divider orientation="vertical" flexItem sx={{ borderColor: '#e2e8f0' }} />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontWeight: 700,
                              color: '#0f172a',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {meeting.title || t('Untitled meeting')}
                          </Typography>
                          <Typography sx={{ color: '#64748b', fontSize: 14 }}>
                            {t('Starts at')} {meeting.startTime || t('TBD')}
                          </Typography>
                        </Box>
                      </Box>
                      <Button
                        variant="contained"
                        onClick={() => openMeetingLink(meeting.url)}
                        disabled={!meeting.url}
                        sx={{
                          bgcolor: '#088330',
                          '&:hover': { bgcolor: '#088330' },
                          borderRadius: 1.5,
                          textTransform: 'none',
                          fontWeight: 700,
                        }}
                      >
                        {t('Join')}
                      </Button>
                    </Box>
                  )})}
                </Box>
              )}
            </Box>
          </Box>

          {/* Right Column */}
          <Box sx={{ display: 'grid', gap: 3, minWidth: 0 }}>
            {/* Quick Actions Panel */}
            <Box sx={{ p: 0 }}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(3, minmax(0, 1fr))', lg: 'repeat(2, minmax(0, 1fr))' },
                  gap: 1.25,
                }}
              >
                <ButtonBase
                  onClick={() => handleOpen('FAQ')}
                  sx={{
                    width: '100%',
                    display: 'grid',
                    justifyItems: 'center',
                    gap: 0.75,
                    p: 1.25,
                    borderRadius: 2.25,
                    bgcolor: '#fff',
                    border: '1px solid #e5e7eb',
                    boxShadow: '0 8px 20px rgba(15, 23, 42, 0.06)',
                    textAlign: 'center',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 12px 24px rgba(15, 23, 42, 0.12)' },
                  }}
                >
                  <Box
                    sx={{
                      width: 72,
                      height: 72,
                      borderRadius: 2,
                      display: 'grid',
                      placeItems: 'center',
                    }}
                  >
                    <HelpOutlineRoundedIcon sx={{ fontSize: 50, color: '#0f172a' }} />
                  </Box>
                  <Typography sx={{ fontWeight: 600, fontSize: 18, color: '#0f172a', textAlign: 'center' }}>
                    {t('(FAQ) الأسئلة المتكررة')}
                  </Typography>
                </ButtonBase>
                <ButtonBase
                  onClick={() => handleOpen('events')}
                  sx={{
                    width: '100%',
                    display: 'grid',
                    justifyItems: 'center',
                    gap: 0.75,
                    p: 1.25,
                    borderRadius: 2.25,
                    bgcolor: '#fff',
                    border: '1px solid #e5e7eb',
                    boxShadow: '0 8px 20px rgba(15, 23, 42, 0.06)',
                    textAlign: 'center',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 12px 24px rgba(15, 23, 42, 0.12)' },
                  }}
                >
                  <Box
                    sx={{
                      width: 72,
                      height: 72,
                      borderRadius: 2,
                      display: 'grid',
                      placeItems: 'center',
                    }}
                  >
                    <EventAvailableRoundedIcon sx={{ fontSize: 50, color: '#404042ff' }} />
                  </Box>
                  <Typography sx={{ fontWeight: 600, fontSize: 18, color: '#0f172a', textAlign: 'center' }}>{t('الفعاليات')}</Typography>
                </ButtonBase>
                <ButtonBase
                  onClick={() => navigate('/teacher/chat')}
                  sx={{
                    width: '100%',
                    display: 'grid',
                    justifyItems: 'center',
                    gap: 0.75,
                    p: 1.25,
                    borderRadius: 2.25,
                    bgcolor: '#fff',
                    border: '1px solid #e5e7eb',
                    boxShadow: '0 8px 20px rgba(15, 23, 42, 0.06)',
                    textAlign: 'center',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 12px 24px rgba(15, 23, 42, 0.12)' },
                  }}
                >
                  <Box
                    sx={{
                      width: 72,
                      height: 72,
                      borderRadius: 2,
                      display: 'grid',
                      placeItems: 'center',
                    }}
                  >
                    {msgCount > 0 ? (
                      <Badge
                        badgeContent={msgCount}
                        color="error"
                        overlap="circular"
                        max={99}
                        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                        sx={{
                          '& .MuiBadge-badge': {
                            minWidth: 26,
                            height: 26,
                            fontSize: 12,
                            fontWeight: 700,
                          },
                        }}
                      >
                        <ForumRoundedIcon sx={{ fontSize: 50, color: '#0f172a' }} />
                      </Badge>
                    ) : (
                      <ForumRoundedIcon sx={{ fontSize: 50, color: '#0f172a' }} />
                    )}
                  </Box>
                  <Typography sx={{ fontWeight: 600, fontSize: 18, color: '#0f172a', textAlign: 'center' }}>{t('صندوق الرسائل')}</Typography>
                </ButtonBase>
                <ButtonBase
                  onClick={() => handleOpen('news')}
                  sx={{
                    width: '100%',
                    display: 'grid',
                    justifyItems: 'center',
                    gap: 0.75,
                    p: 1.25,
                    borderRadius: 2.25,
                    bgcolor: '#fff',
                    border: '1px solid #e5e7eb',
                    boxShadow: '0 8px 20px rgba(15, 23, 42, 0.06)',
                    textAlign: 'center',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 12px 24px rgba(15, 23, 42, 0.12)' },
                  }}
                >
                  <Box
                    sx={{
                      width: 72,
                      height: 72,
                      borderRadius: 2,
                      display: 'grid',
                      placeItems: 'center',
                    }}
                  >
                    <CampaignRoundedIcon sx={{ fontSize: 50, fontWeight: 600, color: '#0f172a' }} />
                  </Box>
                  <Typography sx={{ fontWeight: 600, fontSize: 18, color: '#0f172a', textAlign: 'center' }}>{t('أخبار المؤسسة')}</Typography>
                </ButtonBase>
                <ButtonBase
                  onClick={() => navigate('/teacher/contactUs')}
                  sx={{
                    width: '100%',
                    display: 'grid',
                    justifyItems: 'center',
                    gap: 0.75,
                    p: 1.25,
                    borderRadius: 2.25,
                    bgcolor: '#fff',
                    border: '1px solid #e5e7eb',
                    boxShadow: '0 8px 20px rgba(15, 23, 42, 0.06)',
                    textAlign: 'center',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 12px 24px rgba(15, 23, 42, 0.12)' },
                  }}
                >
                  <Box
                    sx={{
                      width: 72,
                      height: 72,
                      borderRadius: 2,
                      display: 'grid',
                      placeItems: 'center',
                    }}
                  >
                    <FeedbackRoundedIcon sx={{ fontSize: 50, color: '#0f172a' }} />
                  </Box>
                  <Typography sx={{ fontWeight: 600, fontSize: 18, color: '#0f172a', textAlign: 'center' }}>{t('صفحة الأراء و الشكاوى')}</Typography>
                </ButtonBase>
              </Box>
            </Box>

            {/* Messages / Inbox Preview */}
            <Box
              sx={{
                bgcolor: '#fff',
                borderRadius: 2.5,
                p: 3,
                border: '1px solid #eef1f6',
                boxShadow: '0 10px 24px rgba(15, 23, 42, 0.06)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a' }}>
                  {t('Messages / Inbox Preview')}
                </Typography>
                <Chip label={`${msgCount} ${t('Unread')}`} sx={{ bgcolor: '#fee2e2', color: '#b91c1c', fontWeight: 600 }} />
              </Box>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'grid', gap: 1.5 }}>
                {unreadConversations.length === 0 ? (
                  <Box
                    sx={{
                      p: 1.75,
                      borderRadius: 2,
                      bgcolor: '#f8fafc',
                      border: '1px dashed #e2e8f0',
                      textAlign: 'center',
                    }}
                  >
                    <Typography sx={{ fontWeight: 700, color: '#0f172a' }}>
                      {t('No messages yet')}
                    </Typography>
                    <Typography sx={{ color: '#64748b', fontSize: 13 }}>
                      {t('You are all caught up.')}
                    </Typography>
                  </Box>
                ) : (
                  unreadConversations.slice(0, 3).map((convo) => {
                    const name = convo.studentName || role.learnerSingular;
                    const avatarTone = avatarColorsForName(name);
                    const preview = convo.lastMessageText || t('New message');
                    return (
                      <Box
                        key={`unread-${convo.id}`}
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          bgcolor: '#f8fafc',
                          border: '1px solid #eef1f6',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          minWidth: 0,
                        }}
                      >
                        <Box
                          sx={{
                            width: 44,
                            height: 44,
                            borderRadius: '20%',
                            display: 'grid',
                            placeItems: 'center',
                            bgcolor: avatarTone.bg,
                            color: avatarTone.color,
                            fontWeight: 700,
                            fontSize: 16,
                            flexShrink: 0,
                          }}
                        >
                          {initialsFromName(name)}
                        </Box>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography
                            sx={{
                              fontWeight: 700,
                              color: '#0f172a',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {name}
                          </Typography>
                          <Typography
                            sx={{
                              color: '#64748b',
                              fontSize: 13,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {preview}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#ef4444' }} />
                          <Typography sx={{ fontSize: 12, color: '#b91c1c', fontWeight: 700 }}>
                            {t('Unread')}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })
                )}
                <Button
                  variant="contained"
                  onClick={() => navigate('/teacher/chat')}
                  sx={{
                    mt: 1,
                    bgcolor: '#0f172a',
                    '&:hover': { bgcolor: '#1f2937' },
                    borderRadius: 1.5,
                    textTransform: 'none',
                    fontWeight: 700,
                  }}
                >
                  {t('Open inbox')}
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
      {/* Modal Component */}
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '50%',
            height: '50%',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: '8px',
            overflow: 'auto',
          }}
        >
          {(() => {
            switch (modalContent) {
              case 'FAQ':
                return <TeacherFAQ />;
              case 'events':
                return <TeacherEvents />;
              case 'messages':
                return <TeacherMessagesBox />;
              case 'news':
                return <TeacherNews />;
              default:
                return <Typography variant="h6">{t('No content available')}</Typography>;
            }
          })()}
        </Box>
      </Modal>
    </div>
  
  );
}

export default TeacherClasses;
